import React, { useState, useEffect, useCallback } from 'react';
import { ROLES_SISTEMA } from '../../constants/roles';

const AddUserForm = () => {
    // === ESTADOS GENERALES ===
    const [tipoUsuario, setTipoUsuario] = useState('1'); // 1: Ejecutivo, 2: Cliente, 3: Call Center, 4: Proveedor
    const [nombreCompleto, setNombreCompleto] = useState('');
    const [telefono, setTelefono] = useState('');
    const [email, setEmail] = useState('');
    const [usuario, setUsuario] = useState('');
    const [rol, setRol] = useState('');
    const [flexSid, setFlexSid] = useState('');
    // Coordinador y aprobadores por monto
    const [coordinadores, setCoordinadores] = useState<any[]>([]);
    const [coordinadorSeleccionado, setCoordinadorSeleccionado] = useState('');
    const [adminsEspeciales, setAdminsEspeciales] = useState<any[]>([]);
    const [adminEspecialSeleccionado, setAdminEspecialSeleccionado] = useState('');
    const [subdirectores, setSubdirectores] = useState<any[]>([]);
    const [subdirectorSeleccionado, setSubdirectorSeleccionado] = useState('');

    // Proveedor
    const [proveedorSeleccionado, setProveedorSeleccionado] = useState('');

    // UI
    const [activeTab, setActiveTab] = useState('visibilidad');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userStatus, setUserStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
    const [userManuallyEdited, setUserManuallyEdited] = useState(false);
    const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');

    // === ESTADOS DE CATÁLOGOS ===
    const [rolesDisponibles, setRolesDisponibles] = useState<any[]>([]);
    const [clientes, setClientes] = useState<any[]>([]);
    const [proveedores, setProveedores] = useState<any[]>([]); // Para cuando es tipo_usuario == 4

    // Catálogos dinámicos para permisos
    const [contratosDisponibles, setContratosDisponibles] = useState<any[]>([]);
    const [flotillasN1, setFlotillasN1] = useState<any[]>([]);
    const [flotillasN2, setFlotillasN2] = useState<any[]>([]);
    const [flotillasN3, setFlotillasN3] = useState<any[]>([]);

    // === ESTADOS DE TABLAS (VISIBILIDAD Y ARCHIVOS) ===
    const [permisos, setPermisos] = useState<any[]>([]);
    const [archivos, setArchivos] = useState<any[]>([]);

    const [permisoForm, setPermisoForm] = useState({
        cliente: '', nombreCliente: 'Todos',
        contrato: '', nombreContrato: 'Todos',
        nv1: '', nombreNv1: 'Todos',
        nv2: '', nombreNv2: 'Todos',
        nv3: '', nombreNv3: 'Todos'
    });

    const [archivoForm, setArchivoForm] = useState<{ fileObj: File | null; tipo: string; descripcion: string }>({
        fileObj: null, tipo: '1', descripcion: ''
    });

    // === EFECTOS E INICIALIZACIÓN ===
    useEffect(() => {
        fetch('/sittApi/catalogos/obtener_roles.php?tipo=sistema').then(res => res.json()).then(data => setRolesDisponibles(data.data || []));
        fetch('/sittApi/obtener_clientes.php').then(res => res.json()).then(data => setClientes(data.data || []));
        fetch('/sittApi/obtener_usuarios_por_rol.php?key_labels=COORDINADOR,ADMIN_TALLERES')
            .then(res => res.json())
            .then(data => setCoordinadores(data.data || []));
        fetch('/sittApi/obtener_usuarios_por_rol.php?key_labels=ADMIN_ESPECIAL,SYS_ADMIN,GERENTE_CONTRALORIA,ADMIN_CONTRALORIA')
            .then(res => res.json())
            .then(data => setAdminsEspeciales(data.data || []));
        fetch('/sittApi/obtener_usuarios_por_rol.php?key_label=SUBDIRECTOR_OPERACIONES')
            .then(res => res.json())
            .then(data => setSubdirectores(data.data || []));
    }, []);

    useEffect(() => {
        if (nombreCompleto && !userManuallyEdited) {
            let limpio = nombreCompleto.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
            limpio = limpio.replace(/[^a-z\s]/g, "");
            const partes = limpio.split(/\s+/);
            const userGenerado = partes.length == 2 ? partes[0].charAt(0) + partes[1] : partes.length >= 2 ? partes[0].charAt(0) + partes[2] : partes[0];
            setUsuario(userGenerado);
        }
    }, [nombreCompleto, userManuallyEdited]);

    useEffect(() => {
        if (!usuario) { setUserStatus('idle'); return; }
        setUserStatus('checking');
        const delayDebounceFn = setTimeout(async () => {
            try {
                const response = await fetch(`/api/ajaxResponse/verifUsuario.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: `b=${usuario}`
                });
                const text = await response.text();
                setUserStatus(text.trim() === "0" ? 'available' : 'taken');
            } catch (error) {
                setUserStatus('idle');
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [usuario]);

    useEffect(() => {
        if (!email) { setEmailStatus('idle'); return; }
        const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!regexEmail.test(email)) { setEmailStatus('invalid'); return; }
        setEmailStatus('checking');
        const delayDebounceFn = setTimeout(async () => {
            try {
                const response = await fetch(`/api/ajaxResponse/verifCorreo.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: `b=${email}`
                });
                const text = await response.text();
                setEmailStatus(text.trim() === "0" ? 'available' : 'taken');
            } catch (error) {
                setEmailStatus('idle');
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [email]);

    // === LÓGICA DE TABLA DE PERMISOS ===
    const handleClienteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        const nombre = e.target.options[e.target.selectedIndex].text;
        setPermisoForm({
            ...permisoForm, cliente: id, nombreCliente: id ? nombre : 'Todos',
            contrato: '', nombreContrato: 'Todos',
            nv1: '', nombreNv1: 'Todos',
            nv2: '', nombreNv2: 'Todos',
            nv3: '', nombreNv3: 'Todos'
        });
        setContratosDisponibles([]);
        setFlotillasN1([]);
        setFlotillasN2([]);
        setFlotillasN3([]);

        const formData = new FormData();
        formData.append("action", "filtered");
        formData.append("idCliente", btoa(id));
        fetch(`/api/clientes/contratos.php`, { method: "POST", body: formData }).then(res => res.json()).then(data => setContratosDisponibles(data || []));
    };

    const handleContratoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        if (id != "") {
            const nombre = e.target.options[e.target.selectedIndex].text;
            setPermisoForm({
                ...permisoForm, contrato: id, nombreContrato: nombre,
                nv1: '', nombreNv1: 'Todos',
                nv2: '', nombreNv2: 'Todos',
                nv3: '', nombreNv3: 'Todos'
            });
            setFlotillasN1([]);
            setFlotillasN2([]);
            setFlotillasN3([]);

            const formData = new FormData();
            formData.append("action", "listaNivel1");
            formData.append("clave_persona_cliente", permisoForm.cliente);
            formData.append("clave_cliente_contrato", id);
            fetch(`/api/xajax/ajaxCC.php`, { method: "POST", body: formData }).then(res => res.json()).then(data => setFlotillasN1(data || []));
        }
    };

    const handleNv1Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        if (id != "") {
            const nombre = e.target.options[e.target.selectedIndex].text;
            setPermisoForm({
                ...permisoForm, nv1: id, nombreNv1: nombre,
                nv2: '', nombreNv2: 'Todos',
                nv3: '', nombreNv3: 'Todos'
            });
            setFlotillasN2([]);
            setFlotillasN3([]);

            const formData = new FormData();
            formData.append("action", "listaNivel2");
            formData.append("clave_persona_cliente", permisoForm.cliente);
            formData.append("clave_nv1", permisoForm.nv1);
            fetch(`/api/xajax/ajaxCC.php`, { method: "POST", body: formData }).then(res => res.json()).then(data => setFlotillasN2(data || []));
        }
    };

    const handleNv2Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        const nombre = e.target.options[e.target.selectedIndex].text;
        setPermisoForm({
            ...permisoForm, nv2: id, nombreNv2: nombre,
            nv3: '', nombreNv3: 'Todos'
        });
        setFlotillasN3([]);

        const formData = new FormData();
        formData.append("action", "listaNivel3");
        formData.append("clave_persona_cliente", permisoForm.cliente);
        formData.append("clave_nv3", permisoForm.nv1);
        fetch(`/api/xajax/ajaxCC.php`, { method: "POST", body: formData }).then(res => res.json()).then(data => setFlotillasN3(data || []));
    };

    const handleNv3Change = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        const nombre = e.target.options[e.target.selectedIndex].text;
        setPermisoForm({ ...permisoForm, nv3: id, nombreNv3: nombre });
    };

    const agregarPermiso = () => {
        setPermisos([...permisos, { ...permisoForm, id_temp: Date.now() }]);
        setPermisoForm({ cliente: '', nombreCliente: 'Todos', contrato: '', nombreContrato: 'Todos', nv1: '', nombreNv1: 'Todos', nv2: '', nombreNv2: 'Todos', nv3: '', nombreNv3: 'Todos' });
    };

    const eliminarPermiso = (id_temp: number) => {
        setPermisos(permisos.filter(p => p.id_temp !== id_temp));
    };

    // === LÓGICA DE TABLA DE ARCHIVOS ===
    const agregarArchivo = () => {
        if (!archivoForm.fileObj) {
            alert("Debe seleccionar un archivo."); return;
        }
        setArchivos([...archivos, { ...archivoForm, id_temp: Date.now() }]);
        setArchivoForm({ fileObj: null, tipo: '1', descripcion: '' });
        const fileInput = document.getElementById('fileUploaderUser') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    };

    const eliminarArchivo = (id_temp: number) => {
        setArchivos(archivos.filter(a => a.id_temp !== id_temp));
    };

    // === SUBMIT DEL FORMULARIO ===
    const handleSubmit = async () => {
        const rolObj = rolesDisponibles.find(r => r.clave_rol.toString() === rol.toString());
        const esEjecutivo = rolObj?.key_label === ROLES_SISTEMA.EJECUTIVO_FLOTILLA;

        if (userStatus === 'taken') {
            alert("El usuario ya existe, por favor modifícalo."); return;
        }

        if (emailStatus === 'taken') {
            alert("El correo ya esta en uso, por favor utiliza otro."); return;
        }

        if (emailStatus === 'invalid') {
            alert("El correo no es válido, verifica la información."); return;
        }

        if (email === "") {
            alert("El correo es requerido."); return;
        }

        if (nombreCompleto === "") {
            alert("El nombre es requerido."); return;
        }

        if (usuario === "") {
            alert("El usuario es requerido."); return;
        }

        if (rol === "") {
            alert("El rol es es requerido."); return;
        }

        if (esEjecutivo && coordinadorSeleccionado === "") {
            alert("Debe asignar un aprobador de nivel Coordinador (Coordinador o Admin Talleres).");
            return;
        }

        if (esEjecutivo && adminEspecialSeleccionado === "") {
            alert("Debe asignar un aprobador de nivel Admin. Especial (Admin Especial, SYS Admin o Contraloría).");
            return;
        }

        if (esEjecutivo && subdirectorSeleccionado === "") {
            alert("Debe asignar un subdirector de operaciones para el perfil de ejecutivo.");
            return;
        }

        if (permisos.length == 0) {
            alert("Se requiere por lo menos un permiso asignado para continuar."); return;
        }

        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append('clave_tipo_usuario', tipoUsuario);
            formData.append('nombre_completo', nombreCompleto);
            formData.append('telefono', telefono);
            formData.append('email', email);
            formData.append('usuario', usuario);
            formData.append('clave_rol', rol);
            formData.append('flex_sid', flexSid);
            if (esEjecutivo) {
                formData.append('clave_coordinador_asignado', coordinadorSeleccionado);
                formData.append('clave_admin_especial_asignado', adminEspecialSeleccionado);
                formData.append('clave_subdirector_asignado', subdirectorSeleccionado);
            }

            if (tipoUsuario === '4') formData.append('clave_persona_proveedor', proveedorSeleccionado);

            permisos.forEach((permiso, index) => {
                formData.append(`permiso_id[${index}]`, '0');
                formData.append(`permiso_clave_persona_cliente[${index}]`, permiso.cliente);
                formData.append(`permiso_clave_cliente_contrato[${index}]`, permiso.contrato);
                formData.append(`permiso_clave_nv1[${index}]`, permiso.nv1);
                formData.append(`permiso_clave_nv2[${index}]`, permiso.nv2);
                formData.append(`permiso_clave_nv3[${index}]`, permiso.nv3);
            });

            archivos.forEach((archivo, index) => {
                formData.append(`archivero_archivos_clave_tipo_archivo[${index}]`, archivo.tipo);
                formData.append(`archivero_archivos_descripcion[${index}]`, archivo.descripcion);
                formData.append(`file_${index}`, archivo.fileObj);
            });

            const response = await fetch('/sittApi/usuariosAdmin/crear_usuario.php', {
                method: 'POST',
                body: formData
            });
            console.log("erresponseror", response)
            const result = await response.json();

            if (result.status === 'success') {
                alert("Usuario creado correctamente.");
                window.location.href = "/usuarioAdmin";
            } else {
                alert(result.message);
                setIsSubmitting(false);
            }
        } catch (error) {
            console.log("error", error)
            alert("Ocurrió un error al comunicarse con el servidor.");
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container-fluid px-0">
            <div className="card shadow-sm border-0">
                <div className="card-header border-bottom py-3">
                    <h5 className="mb-0 fw-bold"><i className="fas fa-user-plus text-primary me-2"></i> Alta de Nuevo Usuario</h5>
                </div>

                <div className="card-body p-4">
                    <div className="row g-4">

                        {/* === COLUMNA IZQUIERDA: GENERAL === */}
                        <div className="col-xl-4">
                            <div className="card shadow-none border h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold text-primary mb-3 text-uppercase small">Datos Generales</h6>

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-secondary mb-1">Tipo de usuario</label>
                                        <select className="form-select form-select-sm" value={tipoUsuario} onChange={e => setTipoUsuario(e.target.value)} disabled={isSubmitting}>
                                            <option value="1">Usuario SITT</option>
                                        </select>
                                    </div>

                                    {tipoUsuario === '4' && (
                                        <div className="mb-3 animate__animated animate__fadeIn">
                                            <label className="form-label small fw-bold text-secondary mb-1">Proveedor asignado *</label>
                                            <select className="form-select form-select-sm border-warning" required value={proveedorSeleccionado} onChange={e => setProveedorSeleccionado(e.target.value)} disabled={isSubmitting}>
                                                <option value="">Seleccione el proveedor...</option>
                                                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                                            </select>
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-secondary mb-1">Nombre completo *</label>
                                        <input type="text" className="form-control form-control-sm" required value={nombreCompleto} onChange={e => setNombreCompleto(e.target.value)} disabled={isSubmitting} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-secondary mb-1">Teléfono</label>
                                        <input type="text" className="form-control form-control-sm" value={telefono} onChange={e => setTelefono(e.target.value)} disabled={isSubmitting} />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor='email' className="form-label small fw-bold text-secondary mb-1">E-mail *</label>
                                        <div className="input-group input-group-sm">
                                            <input
                                                id='email'
                                                type="email"
                                                className={`form-control ${emailStatus === 'available' ? 'is-valid' : emailStatus === 'taken' ? 'is-invalid' : emailStatus === 'invalid' ? 'is-invalid' : ''}`}
                                                required
                                                value={email}
                                                onChange={e => { setEmail(e.target.value); }}
                                                disabled={isSubmitting}
                                            />
                                            {emailStatus === 'checking' && <span className="input-group-text"><i className="fas fa-spinner fa-spin text-primary"></i></span>}
                                            {emailStatus === 'available' && <span className="input-group-text bg-success border-success"><i className="fas fa-check"></i></span>}
                                            {emailStatus === 'taken' && <span className="input-group-text bg-danger border-danger"><i className="fas fa-times"></i></span>}
                                            {emailStatus === 'invalid' && <span className="input-group-text bg-danger border-danger"><i className="fas fa-times"></i></span>}
                                        </div>
                                        {emailStatus === 'taken' && <div className="invalid-feedback d-block small">Este correo ya está en uso.</div>}
                                        {emailStatus === 'invalid' && <div className="invalid-feedback d-block small">El correo no tiene formato válido.</div>}
                                    </div>

                                    <hr className="my-4" />
                                    <h6 className="fw-bold text-primary mb-3 text-uppercase small">Credenciales de Acceso</h6>

                                    <div className="mb-3">
                                        <label htmlFor='usuario' className="form-label small fw-bold text-secondary mb-1">Usuario *</label>
                                        <div className="input-group input-group-sm">
                                            <input
                                                id='usuario'
                                                type="text"
                                                className={`form-control ${userStatus === 'available' ? 'is-valid' : userStatus === 'taken' ? 'is-invalid' : ''}`}
                                                required
                                                value={usuario}
                                                onChange={e => { setUsuario(e.target.value); setUserManuallyEdited(true); }}
                                                disabled={isSubmitting}
                                            />
                                            {userStatus === 'checking' && <span className="input-group-text"><i className="fas fa-spinner fa-spin text-primary"></i></span>}
                                            {userStatus === 'available' && <span className="input-group-text bg-success border-success"><i className="fas fa-check"></i></span>}
                                            {userStatus === 'taken' && <span className="input-group-text bg-danger border-danger"><i className="fas fa-times"></i></span>}
                                        </div>
                                        {userStatus === 'available' && <div className="valid-feedback d-block small">Usuario disponible.</div>}
                                        {userStatus === 'taken' && <div className="invalid-feedback d-block small">Este usuario ya existe.</div>}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-secondary mb-1">Rol en el sistema *</label>
                                        <select className="form-select form-select-sm" required value={rol} onChange={e => setRol(e.target.value)} disabled={isSubmitting}>
                                            <option value="">Seleccione el rol...</option>
                                            {rolesDisponibles.map(r => (
                                                <option key={r.clave_rol} value={r.clave_rol}>{r.rol}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {rolesDisponibles.find(r => r.clave_rol.toString() === rol.toString())?.key_label === ROLES_SISTEMA.EJECUTIVO_FLOTILLA && (
                                        <>
                                        <div className="mb-3 animate__animated animate__fadeInDown">
                                            <label className="form-label small fw-bold text-primary mb-1">Aprobador nivel Coordinador *</label>
                                            <select
                                                className="form-select form-select-sm border-primary"
                                                required
                                                value={coordinadorSeleccionado}
                                                onChange={e => setCoordinadorSeleccionado(e.target.value)}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Seleccione al aprobador...</option>
                                                {coordinadores.map(c => (
                                                    <option key={c.clave_usuario} value={c.clave_usuario}>{c.nombre_completo} - {c.usuario} ({c.rol || c.key_label})</option>
                                                ))}
                                            </select>
                                            <div className="form-text text-muted" style={{ fontSize: '0.70rem' }}>
                                                Roles: Coordinador o Administrador de Talleres. Aprueba OT con monto ≤ $15,000
                                            </div>
                                        </div>
                                        <div className="mb-3 animate__animated animate__fadeInDown">
                                            <label className="form-label small fw-bold text-primary mb-1">Aprobador nivel Admin. Especial *</label>
                                            <select
                                                className="form-select form-select-sm border-primary"
                                                required
                                                value={adminEspecialSeleccionado}
                                                onChange={e => setAdminEspecialSeleccionado(e.target.value)}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Seleccione al aprobador...</option>
                                                {adminsEspeciales.map(c => (
                                                    <option key={c.clave_usuario} value={c.clave_usuario}>{c.nombre_completo} - {c.usuario} ({c.rol || c.key_label})</option>
                                                ))}
                                            </select>
                                            <div className="form-text text-muted" style={{ fontSize: '0.70rem' }}>
                                                Roles: Administrador Especial, SYS Admin o Contraloría. Aprueba OT con monto &gt; $15,000 y &lt; $50,000
                                            </div>
                                        </div>
                                        <div className="mb-3 animate__animated animate__fadeInDown">
                                            <label className="form-label small fw-bold text-primary mb-1">Subdirector de Operaciones *</label>
                                            <select
                                                className="form-select form-select-sm border-primary"
                                                required
                                                value={subdirectorSeleccionado}
                                                onChange={e => setSubdirectorSeleccionado(e.target.value)}
                                                disabled={isSubmitting}
                                            >
                                                <option value="">Seleccione al subdirector...</option>
                                                {subdirectores.map(c => (
                                                    <option key={c.clave_usuario} value={c.clave_usuario}>{c.nombre_completo} - {c.usuario}</option>
                                                ))}
                                            </select>
                                            <div className="form-text text-muted" style={{ fontSize: '0.70rem' }}>
                                                Aprueba OT con monto ≥ $50,000
                                            </div>
                                        </div>
                                        </>
                                    )}
                                    <p className="text-muted fst-italic" style={{ fontSize: '0.75rem' }}><i className="fas fa-envelope me-1"></i> El usuario recibirá un correo con instrucciones para generar su contraseña.</p>
                                </div>
                            </div>
                        </div>

                        {/* === COLUMNA DERECHA: PESTAÑAS (Visibilidad, Archivos, Flex) === */}
                        <div className="col-xl-8">
                            <div className="card shadow-none border h-100">
                                <div className="card-header p-0 border-bottom">
                                    <ul className="nav nav-tabs border-0 px-3 pt-2">
                                        <li className="nav-item">
                                            <button type="button" className={`nav-link border-0 ${activeTab === 'visibilidad' ? 'active fw-bold text-primary border-bottom border-primary border-3' : 'text-muted'}`} onClick={() => setActiveTab('visibilidad')}><i className="far fa-eye me-1"></i> Visibilidad y Contratos</button>
                                        </li>
                                        <li className="nav-item">
                                            <button type="button" className={`nav-link border-0 ${activeTab === 'archivos' ? 'active fw-bold text-primary border-bottom border-primary border-3' : 'text-muted'}`} onClick={() => setActiveTab('archivos')}><i className="far fa-folder me-1"></i> Expediente</button>
                                        </li>
                                        <li className="nav-item">
                                            <button type="button" className={`nav-link border-0 ${activeTab === 'flex' ? 'active fw-bold text-primary border-bottom border-primary border-3' : 'text-muted'}`} onClick={() => setActiveTab('flex')}><i className="far fa-headset me-1"></i> Integración Twilio Flex</button>
                                        </li>
                                    </ul>
                                </div>

                                <div className="card-body">
                                    {/* TAB: VISIBILIDAD */}
                                    {activeTab === 'visibilidad' && (
                                        <div className="row animate__animated animate__fadeIn">
                                            <div className="col-md-4 border-end pe-4">
                                                <h6 className="fw-bold small text-uppercase mb-3">Agregar Permiso</h6>

                                                <div className="mb-2">
                                                    <label className="form-label small text-secondary mb-1">Cliente</label>
                                                    <select className="form-select form-select-sm" value={permisoForm.cliente} onChange={handleClienteChange}>
                                                        <option value="">Todos los clientes</option>
                                                        {clientes.map(c => <option key={c.clave_persona_cliente} value={c.clave_persona_cliente}>{c.nombre_completo}</option>)}
                                                    </select>
                                                </div>

                                                <div className="mb-2">
                                                    <label className="form-label small text-secondary mb-1">Contrato</label>
                                                    <select className="form-select form-select-sm" value={permisoForm.contrato} onChange={handleContratoChange} disabled={!permisoForm.cliente}>
                                                        <option value="">Todos los contratos</option>
                                                        {contratosDisponibles.map(c => <option key={c.clave_cliente_contrato} value={c.clave_cliente_contrato}>{c.nombre}</option>)}
                                                    </select>
                                                </div>

                                                {contratosDisponibles.length > 0 && (
                                                    <>
                                                        <div className="mb-2">
                                                            <label className="form-label small text-secondary mb-1">Nivel 1</label>
                                                            <select className="form-select form-select-sm" value={permisoForm.nv1} onChange={handleNv1Change} disabled={!permisoForm.contrato || flotillasN1.length == 0}>
                                                                <option value="">{flotillasN1.length > 0 ? "Elegir..." : "Sin niveles"}</option>
                                                                {flotillasN1.map(c => <option key={c.clave_nv1} value={c.clave_nv1}>{c.descripcion_nv1}</option>)}
                                                            </select>
                                                        </div>

                                                        {flotillasN1.length > 0 && (
                                                            <>
                                                                <div className="mb-2">
                                                                    <label className="form-label small text-secondary mb-1">Nivel 2</label>
                                                                    <select className="form-select form-select-sm" value={permisoForm.nv2} onChange={handleNv2Change} disabled={!permisoForm.nv1 || flotillasN2.length == 0}>
                                                                        <option value="">{flotillasN2.length > 0 ? "Elegir..." : "Sin niveles"}</option>
                                                                        {flotillasN2.map(c => <option key={c.clave_nv2} value={c.clave_nv2}>{c.descripcion_nv2}</option>)}
                                                                    </select>
                                                                </div>

                                                                {flotillasN2.length > 0 && (
                                                                    <>
                                                                        <div className="mb-2">
                                                                            <label className="form-label small text-secondary mb-1">Nivel 3</label>
                                                                            <select className="form-select form-select-sm" value={permisoForm.nv3} onChange={handleNv3Change} disabled={!permisoForm.nv2 || flotillasN3.length == 0}>
                                                                                <option value="">{flotillasN3.length > 0 ? "Elegir..." : "Sin niveles"}</option>
                                                                                {flotillasN3.map(c => <option key={c.clave_nv3} value={c.clave_nv3}>{c.descripcion_nv3}</option>)}
                                                                            </select>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}

                                                    </>
                                                )}

                                                <button type="button" className="btn btn-outline-primary btn-sm w-100 mt-3 fw-bold" onClick={agregarPermiso} disabled={isSubmitting}>
                                                    <i className="fas fa-plus me-1"></i> Asignar Permiso
                                                </button>
                                            </div>

                                            <div className="col-md-8 ps-4">
                                                <div className="table-responsive">
                                                    <table className="table table-hover table-sm align-middle border">
                                                        <thead className="text-secondary small">
                                                            <tr>
                                                                <th>Cliente</th>
                                                                <th>Contrato</th>
                                                                <th>Nivel 1</th>
                                                                <th>Nivel 2</th>
                                                                <th>Nivel 3</th>
                                                                <th className="text-center"><i className="fas fa-cog"></i></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {permisos.length === 0 ? (
                                                                <tr><td colSpan={6} className="text-center text-muted py-4 fst-italic">Sin restricciones específicas asignadas.</td></tr>
                                                            ) : (
                                                                permisos.map(p => (
                                                                    <tr key={p.id_temp}>
                                                                        <td className="small">{p.nombreCliente}</td>
                                                                        <td className="small">{p.nombreContrato}</td>
                                                                        <td className="small">{p.nombreNv1}</td>
                                                                        <td className="small">{p.nombreNv2}</td>
                                                                        <td className="small">{p.nombreNv3}</td>
                                                                        <td className="text-center">
                                                                            <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => eliminarPermiso(p.id_temp)}><i className="fas fa-trash-alt"></i></button>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* TAB: ARCHIVOS */}
                                    {activeTab === 'archivos' && (
                                        <div className="row animate__animated animate__fadeIn">
                                            <div className="col-md-4 border-end pe-4">
                                                <h6 className="fw-bold small text-uppercase mb-3">Adjuntar Documento</h6>
                                                <div className="mb-2">
                                                    <label className="form-label small text-secondary mb-1">Archivo</label>
                                                    <input id="fileUploaderUser" type="file" className="form-control form-control-sm" onChange={e => setArchivoForm({ ...archivoForm, fileObj: e.target.files?.[0] || null })} disabled={isSubmitting} />
                                                </div>
                                                <div className="mb-2">
                                                    <label className="form-label small text-secondary mb-1">Clasificación</label>
                                                    <select className="form-select form-select-sm" value={archivoForm.tipo} onChange={e => setArchivoForm({ ...archivoForm, tipo: e.target.value })} disabled={isSubmitting}>
                                                        <option value="1">Identificación Oficial (INE/IFE)</option>
                                                        <option value="2">Comprobante de Domicilio</option>
                                                        <option value="3">Contrato Firmado</option>
                                                    </select>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label small text-secondary mb-1">Descripción corta</label>
                                                    <textarea className="form-control form-control-sm" rows={2} value={archivoForm.descripcion} onChange={e => setArchivoForm({ ...archivoForm, descripcion: e.target.value })} disabled={isSubmitting}></textarea>
                                                </div>
                                                <button type="button" className="btn btn-outline-primary btn-sm w-100 fw-bold" onClick={agregarArchivo} disabled={isSubmitting || !archivoForm.fileObj}>
                                                    <i className="fas fa-upload me-1"></i> Subir a Expediente
                                                </button>
                                            </div>

                                            <div className="col-md-8 ps-4">
                                                <div className="table-responsive">
                                                    <table className="table table-hover table-sm align-middle border">
                                                        <thead className=" text-secondary small">
                                                            <tr>
                                                                <th>Documento</th>
                                                                <th>Clasificación</th>
                                                                <th>Notas</th>
                                                                <th className="text-center"><i className="fas fa-cog"></i></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {archivos.length === 0 ? (
                                                                <tr><td colSpan={4} className="text-center text-muted py-4 fst-italic">Expediente vacío.</td></tr>
                                                            ) : (
                                                                archivos.map(a => (
                                                                    <tr key={a.id_temp}>
                                                                        <td className="small fw-bold text-primary text-truncate" style={{ maxWidth: '150px' }}><i className="fas fa-file-alt me-2"></i>{a.fileObj.name}</td>
                                                                        <td className="small">{a.tipo === '1' ? 'INE' : a.tipo === '2' ? 'Domicilio' : 'Contrato'}</td>
                                                                        <td className="small text-truncate" style={{ maxWidth: '150px' }}>{a.descripcion || '-'}</td>
                                                                        <td className="text-center">
                                                                            <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => eliminarArchivo(a.id_temp)}><i className="fas fa-trash-alt"></i></button>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* TAB: TWILIO FLEX */}
                                    {activeTab === 'flex' && (
                                        <div className="row animate__animated animate__fadeIn">
                                            <div className="col-md-6">
                                                <h6 className="fw-bold small text-uppercase mb-3">Parámetros de Integración</h6>
                                                <div className="mb-3">
                                                    <label className="form-label small fw-bold text-secondary mb-1">SID de Trabajador (Flex)</label>
                                                    <input type="text" className="form-control form-control-sm" placeholder="WKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" value={flexSid} onChange={e => setFlexSid(e.target.value)} disabled={isSubmitting} />
                                                    <div className="form-text" style={{ fontSize: '0.75rem' }}>Necesario únicamente para usuarios tipo Call Center que requieran recibir llamadas.</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="card-footer border-top py-3 text-end">
                    <a href="/usuarioAdmin" className="btn btn-secondary border text-muted px-4 me-2 fw-bold" onClick={(e) => { if (!confirm('¿Desea descartar los cambios?')) e.preventDefault(); }}>Cancelar</a>
                    <button type="button" onClick={handleSubmit} className="btn btn-primary px-5 fw-bold shadow-sm" disabled={isSubmitting || userStatus === 'taken' || userStatus === 'checking'}>
                        {isSubmitting ? <><i className="fas fa-spinner fa-spin me-2"></i> Creando Perfil...</> : <><i className="fas fa-save me-2"></i> Confirmar Alta</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddUserForm;